import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function FaradaysLawCalculator() {
  const [turns, setTurns] = useState("");
  const [area, setArea] = useState("");
  const [fieldChange, setFieldChange] = useState("");
  const [timeChange, setTimeChange] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateEMF = () => {
    const n = parseFloat(turns);
    const a = parseFloat(area);
    const db = parseFloat(fieldChange);
    const dt = parseFloat(timeChange);

    if (isNaN(n) || isNaN(a) || isNaN(db) || isNaN(dt) || dt === 0) {
      toast.error("Please enter valid values (time must be non-zero)");
      return;
    }

    const emf = n * a * (db / dt);
    setResult(Math.abs(emf));
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setTurns("");
    setArea("");
    setFieldChange("");
    setTimeChange("");
    setResult(null);
  };

  return (
    <PageLayout calculatorName="Faraday's Law Calculator" calculatorPath="/calculator/faradays-law">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Faraday's Law Calculator</h1>
          <p className="text-muted-foreground">
            Calculate induced EMF from changing magnetic flux
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: ε = -N(dΦ/dt) = -NA(dB/dt)</CardTitle>
            <CardDescription>
              Induced EMF from changing magnetic field
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="turns">Number of Turns (N)</Label>
                <Input
                  id="turns"
                  type="number"
                  step="1"
                  placeholder="Enter number of coil turns"
                  value={turns}
                  onChange={(e) => setTurns(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="area">Loop Area (m²)</Label>
                <Input
                  id="area"
                  type="number"
                  step="any"
                  placeholder="Enter coil area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fieldChange">Change in B-field (T)</Label>
                <Input
                  id="fieldChange"
                  type="number"
                  step="any"
                  placeholder="ΔB = B_final - B_initial"
                  value={fieldChange}
                  onChange={(e) => setFieldChange(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeChange">Time Interval (s)</Label>
                <Input
                  id="timeChange"
                  type="number"
                  step="any"
                  placeholder="Δt"
                  value={timeChange}
                  onChange={(e) => setTimeChange(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateEMF} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(4)} V
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Induced EMF (Electromotive Force)
                    </p>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["faradays-law"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• EMF induced when magnetic flux changes</p>
                <p>• Lenz's Law: induced current opposes the change</p>
                <p>• Faster changes produce larger EMF</p>
                <p>• More turns = more EMF</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 100-turn coil, 0.1 m² area, B changes 0.5 T in 0.1 s = 50 V
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
